import { useState } from 'react';
import {
  type LabTest,
  type TestParameter,
  type TestProcedureRow,
  createTestProcedureSchema,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import {
  InterpretationCard,
  ProcedureCoverageCard,
  ProcedureStepsCard,
  SampleAndMethodCard,
} from './ProcedureFormSections';
import {
  type ProcedureLevel,
  type ProcedureValues,
  clearOtherTarget,
  initialProcedureLevel,
  initialProcedureValues,
  initialSteps,
} from './procedure-form.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.testProcedures;

export function TestProcedureFormPage() {
  return (
    <ResourceFormShell<TestProcedureRow> path={PATH} noun="procedure">
      {({ id, record }) => <ProcedureForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

function ProcedureForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: TestProcedureRow | undefined;
}) {
  const tests = useAllRecords<LabTest>({ path: RESOURCE.labTests });
  const parameters = useAllRecords<TestParameter>({ path: RESOURCE.testParameters });

  const [level, setLevel] = useState<ProcedureLevel>(initialProcedureLevel(record));
  const [steps, setSteps] = useState<string[]>(initialSteps(record));

  const { values, set, patch } = useFormValues<ProcedureValues>(initialProcedureValues(record));

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createTestProcedureSchema,
    noun: 'Procedure',
  });

  function handleLevelChange(next: ProcedureLevel) {
    setLevel(next);
    patch(clearOtherTarget(next));
  }

  return (
    <form onSubmit={form.handleSubmit({ ...values, procedure_steps: steps })} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <ProcedureCoverageCard
          values={values}
          onFieldChange={set}
          errors={form.errors}
          level={level}
          onLevelChange={handleLevelChange}
          tests={tests.items}
          parameters={parameters.items}
        />

        <SampleAndMethodCard values={values} onFieldChange={set} />

        <ProcedureStepsCard steps={steps} onStepsChange={setSteps} />

        <InterpretationCard values={values} onFieldChange={set} />

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save as a new version' : 'Save the procedure'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
